import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Card, CardContent } from "@volca/ui/components/ui/card";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useApForwarding } from "@/accounts-payable/query/forwarding";
import { getHelpTopicPath } from "@/help/routes";
import { EmailForwardingInstructions } from "@/onboarding/simple-onboarding/email-forwarding-instructions";

export const ForwardingEmptyState = ({
  businessId,
  children,
  onBrowseInvoices,
}: {
  businessId: string | null;
  children: ReactNode;
  onBrowseInvoices?: () => void;
}) => {
  const query = useApForwarding(businessId);
  if (query.isLoading || (businessId && !query.isFetchedAfterMount))
    return (
      <Spinner layout="centered" aria-label="Loading forwarding instructions" />
    );
  if (query.isError)
    return (
      <Stack gap={3} align="start" className="mx-auto w-full max-w-2xl py-6">
        <Alert color="danger">
          Could not load forwarding instructions. Please try again.
        </Alert>
        <Button
          variant="outline"
          loading={query.isFetching}
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          Try again
        </Button>
      </Stack>
    );
  if (!query.data || query.data.has_processed_invoices) return children;

  return (
    <Stack gap={4} align="stretch" className="mx-auto w-full max-w-2xl py-6">
      <Stack gap={1} align="start">
        <Text variant="title">Get your invoices into Volca</Text>
        <Text tone="muted">
          Set up forwarding so vendor invoices arrive here automatically. You
          can also use Upload to add an invoice now.
        </Text>
      </Stack>
      <Card className="gap-0 py-0">
        <CardContent className="p-5">
          <EmailForwardingInstructions
            address={query.data.ap_inbound_email}
            check={query.data.forwarding_check}
            embedded
          />
        </CardContent>
      </Card>
      <Stack direction="row" gap={4} wrap align="center">
        <Button
          variant="link"
          nativeButton={false}
          render={
            <Link
              to={{
                pathname: getHelpTopicPath(
                  "accounts-payable/emails/forwarding",
                ),
                search: businessId
                  ? `?business=${encodeURIComponent(businessId)}`
                  : undefined,
              }}
            />
          }
        >
          Read the Volca forwarding guide
        </Button>
        {onBrowseInvoices && (
          <Button
            variant="link"
            className="self-start"
            onClick={onBrowseInvoices}
          >
            View uploaded invoices
          </Button>
        )}
      </Stack>
    </Stack>
  );
};
